import React, { useState, useEffect, useCallback } from "react";
import "@fortawesome/fontawesome-free/css/all.min.css";
import {
  faHeart,
  faComment,
  faShare,
  faEye,
  faRetweet,
  faQuoteRight,
  faReply,
  faUpRightFromSquare,
  faArrowUp,
  faArrowDown,
  faTimes
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Loader from "../loader.js";

import OTSettingBar from "./OTFilterBar";
import "./OTCardContainer.css";
import axios from "axios";

const OTCardContainer = () => {
  const [sortOrders, setSortOrders] = useState({});
  const [searches, setSearches] = useState([]);
  const [searchResults, setSearchResults] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [expandedText, setExpandedText] = useState(new Set());
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    platforms: [],
    startDate: null,
    endDate: null,
  });

  // Load saved data from localStorage on component mount
  useEffect(() => {
    const savedFilters = localStorage.getItem("filtersState");
    const savedSearches = localStorage.getItem("searchesState");
    const savedResults = localStorage.getItem("searchResultsState");

    if (savedFilters) {
      const { platforms, startDate, endDate } = JSON.parse(savedFilters);
      setFilters({
        platforms: platforms || [],
        startDate: startDate || null,
        endDate: endDate || null,
      });
    }

    if (savedSearches) {
      setSearches(JSON.parse(savedSearches) || []);
    }

    if (savedResults) {
      setSearchResults(JSON.parse(savedResults) || {});
    }
  }, []);

  // Save state to localStorage whenever it changes
  // useEffect(() => {
  //   localStorage.setItem("filtersState", JSON.stringify(filters));
  // }, [filters]);

  // useEffect(() => {
  //   localStorage.setItem("searchesState", JSON.stringify(searches));
  // }, [searches]);

  // useEffect(() => {
  //   localStorage.setItem("searchResultsState", JSON.stringify(searchResults));
  // }, [searchResults]);

  // Function to toggle sorting order for a specific platform
  const handleSortOrder = (platform) => {
    setSortOrders((prevSortOrders) => ({
      ...prevSortOrders,
      [platform]: prevSortOrders[platform] === "asc" ? "desc" : "asc",
    }));
  };

  // Add a new search term and fetch its results
  const applySearch = (searchTerm) => {
    if (!searchTerm.trim() || searches.includes(searchTerm.trim())) return;
    
    const newSearches = [...searches, searchTerm.trim()];
    setSearches(newSearches);
    fetchPosts(searchTerm.trim());
  };

  // Remove a search term and its results
  const removeSearchTerm = (termToRemove) => {
    setSearches(prevSearches => prevSearches.filter(term => term !== termToRemove));
    setSearchResults(prevResults => {
      const newResults = { ...prevResults };
      delete newResults[termToRemove];
      return newResults;
    });
  };

  const fetchPosts = async (searchTerm) => {
    if (!searchTerm.trim()) {
      setError("Please enter at least one keyword.");
      return;
    }

    setIsLoading(true);
    setError("");
    try {
      const response = await axios.get("api/search/", {
        params: { keyword: searchTerm },
      });
      
      // Store results under the search term
      setSearchResults(prev => ({
        ...prev,
        [searchTerm]: response.data
      }));
    } catch (err) {
      console.error("Error fetching posts:", err);
      setError(
        err.response?.data?.error || "Failed to fetch posts. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const resetFilters = () => {
    setFilters({
      platforms: [],
      startDate: null,
      endDate: null,
    });
  };

  const applyFilters = (filterValues) => {
    setFilters(filterValues);
  };

  const filterPostsByDate = (posts) => {
    if (!filters.startDate && !filters.endDate) return posts;
  
    const startDate = filters.startDate ? new Date(filters.startDate) : null;
    const endDate = filters.endDate ? new Date(filters.endDate) : null;
    
    if (startDate) startDate.setHours(0, 0, 0, 0);
    if (endDate) endDate.setHours(23, 59, 59, 999);
  
    return posts.filter((post) => {
      const postDate = new Date(post.timestamp * 1000);
      return (
        (!startDate || postDate >= startDate) && 
        (!endDate || postDate <= endDate)
      );
    });
  };

  const highlightText = (text, keywords) => {
    if (!keywords || !keywords.length) return text;

    // Create a regex pattern for all keywords
    const pattern = keywords
      .map((keyword) => keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .join("|");

    const regex = new RegExp(`(${pattern})`, "gi");

    // Split text into parts that should be highlighted and those that shouldn't
    const parts = text.split(regex);

    return parts.map((part, index) => {
      // Check if this part matches any keyword (case-insensitive)
      const isMatch = keywords.some(
        (keyword) => part.toLowerCase() === keyword.toLowerCase()
      );

      return isMatch ? (
        <span
          key={index}
          className="highlighted-term"
          style={{
            backgroundColor: "#ffeb3b",
            padding: "0 2px",
            borderRadius: "2px",
          }}
        >
          {part}
        </span>
      ) : (
        part
      );
    });
  };

  const toPakistaniTime = (timestamp) => {
    const date = new Date(timestamp * 1000);
  
    const options = {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true
    };
  
    return new Intl.DateTimeFormat('en-GB', options).format(date);
  };

  const renderOriginatorText = (searchTerm, platform, index) => {
    if (index < 10) {
      const platformClass = `${platform.toLowerCase()}-originator`;
      return (
        <div className={`originator-text ${platformClass}`}>
          <span className="originator-term">{searchTerm}</span>
          <span className="originator-number">{index + 1}</span>
        </div>
      );
    }
    return null;
  };

  const renderPostText = useCallback(
    (text, id) => {
      const maxTextLength = 100;
      const isExpanded = expandedText.has(id);

      const toggleExpand = () => {
        const newSet = new Set(expandedText);
        if (newSet.has(id)) {
          newSet.delete(id);
        } else {
          newSet.add(id);
        }
        setExpandedText(newSet);
      };

      let displayText = text;
      if (!isExpanded && text.length > maxTextLength) {
        displayText = text.substring(0, maxTextLength) + "...";
      }

      // Find which search term this post matches for highlighting
      let matchingTerms = [];
      searches.forEach(term => {
        if (text.toLowerCase().includes(term.toLowerCase())) {
          matchingTerms.push(term);
        }
      });

      return (
        <div className="post-text">
          <p>
            {highlightText(displayText, matchingTerms)}
            {text.length > maxTextLength && (
              <span
                style={{ color: "blue", cursor: "pointer", marginLeft: "5px" }}
                onClick={toggleExpand}
              >
                {isExpanded ? "Show Less" : "Read More"}
              </span>
            )}
          </p>
        </div>
      );
    },
    [expandedText, searches]
  );

  const renderPostImages = (images) => {
    if (!images) return null;

    // Normalize images to be an array
    if (!Array.isArray(images)) {
      images = [images];
    }

    return (
      <div className="post-images-container">
        {images.map((url, index) => (
          <img
            key={index}
            src={url}
            alt={`${index + 1}`}
            style={{ maxWidth: "100%", borderRadius: "8px", margin: "5px" }}
          />
        ))}
      </div>
    );
  };

  const renderPostStats = useCallback((post) => {
    const { platform } = post;

    switch (platform) {
      case "facebook":
        return (
          <>
            <div className="stat-item">
              <FontAwesomeIcon icon={faHeart} />{" "}
              {post.reactions_total_count || 0}
            </div>
            <div className="stat-item">
              <FontAwesomeIcon icon={faComment} /> {post.comments_count || 0}
            </div>
            <div className="stat-item">
              <FontAwesomeIcon icon={faShare} /> {post.shares_count || 0}
            </div>
            <div className="stat-item">
              <FontAwesomeIcon icon={faEye} /> {post.video_view_count || 0}
            </div>
          </>
        );

      case "tiktok":
        return (
          <>
            <div className="stat-item">
              <FontAwesomeIcon icon={faShare} /> {post.share_count || 0}
            </div>
            <div className="stat-item">
              <FontAwesomeIcon icon={faEye} /> {post.play_count || 0}
            </div>
            <div className="stat-item">
              <FontAwesomeIcon icon={faHeart} /> {post.digg_count || 0}
            </div>
            <div className="stat-item">
              <FontAwesomeIcon icon={faComment} /> {post.comment_count || 0}
            </div>
          </>
        );

      case "instagram":
        return (
          <>
            <div className="stat-item">
              <FontAwesomeIcon icon={faHeart} /> {post.likes_count || 0}
            </div>
            <div className="stat-item">
              <FontAwesomeIcon icon={faComment} /> {post.comments_count || 0}
            </div>
            <div className="stat-item">
              <FontAwesomeIcon icon={faEye} /> {post.video_views_count || 0}
            </div>
          </>
        );
      case "twitter":
        return (
          <>
            <div className="stat-item">
              <FontAwesomeIcon icon={faEye} /> {post.view_count || 0}
            </div>
            <div className="stat-item">
              <FontAwesomeIcon icon={faQuoteRight} /> {post.quote_count || 0}
            </div>
            <div className="stat-item">
              <FontAwesomeIcon icon={faReply} /> {post.reply_count || 0}
            </div>
            <div className="stat-item">
              <FontAwesomeIcon icon={faHeart} /> {post.favorite_count || 0}
            </div>
            <div className="stat-item">
              <FontAwesomeIcon icon={faRetweet} /> {post.retweet_count || 0}
            </div>
          </>
        );

      default:
        return null;
    }
  }, []);

  const platformStyles = {
    twitter: { backgroundColor: "#1DA1F2" },
    facebook: { backgroundColor: "#3b5998" },
    instagram: {
      backgroundImage:
        "linear-gradient(45deg, #feda75, #fa7e1e, #d62976, #962fbf, #4f5bd5)",
    },
    tiktok: { backgroundImage: "linear-gradient(45deg, #69C9D0, #9300C7)" },
    default: { backgroundColor: "#707070" },
  };

  const generatePostUrl = (post, platform) => {
    if (!post.id) return null; // Ensure post ID exists

    switch (platform) {
      case "twitter":
        return `https://twitter.com/${post.author_username}/status/${post.id}`;
      case "facebook":
        return `https://www.facebook.com/${post.id}`;
      case "instagram":
        return `https://www.instagram.com/p/${post.shortcode}`;
      case "tiktok":
        return `https://www.tiktok.com/${post.author_username}/video/${post.id}`;
      default:
        return "#"; // Fallback URL
    }
  };

  const renderOpenPostButton = (post, platform) => {
    const postUrl = generatePostUrl(post, platform);
    if (!postUrl) return null; // Don't render if no valid URL

    return (
      <div className="open-post-container">
        <a
          href={postUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="open-post-button"
          title="Open original post"
        >
          <FontAwesomeIcon
            icon={faUpRightFromSquare}
            style={{
              color:
                platform === "twitter"
                  ? "#1DA1F2"
                  : platform === "facebook"
                  ? "#3b5998"
                  : platform === "instagram"
                  ? "#d62976"
                  : platform === "tiktok"
                  ? "#69C9D0"
                  : "#707070",
              transition: "color 0.3s ease-in-out",
            }}
            onMouseEnter={(e) => (e.target.style.color = "#63E6BE")}
            onMouseLeave={(e) =>
              (e.target.style.color =
                platform === "twitter"
                  ? "#1DA1F2"
                  : platform === "facebook"
                  ? "#3b5998"
                  : platform === "instagram"
                  ? "#d62976"
                  : platform === "tiktok"
                  ? "#69C9D0"
                  : "#707070")
            }
          />
        </a>
      </div>
    );
  };

  const getPlatformStyle = (platform) =>
    platformStyles[platform] || platformStyles.default;

  // Group posts by platform for each search term
  const groupPostsByPlatform = (posts) => {
    return posts.reduce((acc, post) => {
      if (!acc[post.platform]) {
        acc[post.platform] = [];
      }
      acc[post.platform].push(post);
      return acc;
    }, {});
  };

  return (
    <div className="OT-main-content">
      <OTSettingBar
        applySearch={applySearch}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
        activeSearchTerms={searches}
        removeSearchTerm={removeSearchTerm}
      />
      
      {isLoading && <Loader />}
      {error && <p className="error">{error}</p>}
      
      {/* Display message when no search terms */}
      {searches.length === 0 && !isLoading && (
        <div className="no-search-message">
          <p>Enter a search term to see results.</p>
        </div>
      )}

      <div className="search-terms-container">
        {searches.map((searchTerm) => {
          if (!searchResults[searchTerm]) return null;
          
          const groupedPosts = groupPostsByPlatform(searchResults[searchTerm]);
          
          return (
            <div key={searchTerm} className="search-term-section">
         
              
              <div id="card-container" className="card-container">
                {Object.keys(groupedPosts)
                  .filter(
                    (platform) =>
                      filters.platforms.length === 0 ||
                      filters.platforms.includes(platform)
                  )
                  .map((platform) => {
                    let filteredPosts = filterPostsByDate(groupedPosts[platform]);

                    // Get sorting order for this platform, default to "desc"
                    const sortOrder = sortOrders[platform] || "desc";

                    // Sort posts by date for this platform only
                    filteredPosts = [...filteredPosts].sort((a, b) =>
                      sortOrder === "asc"
                        ? a.timestamp - b.timestamp
                        : b.timestamp - a.timestamp
                    );

                    if (filteredPosts.length > 0) {
                      return (
                        <div className="card" data-platform={platform} key={`${searchTerm}-${platform}`}>
                          <div
                            className="OT-card-header"
                            style={getPlatformStyle(platform)}
                          >
                            <span>{platform.toUpperCase()}</span>

                            {/* Sorting Arrows with Hover Tooltips */}
                            <div className="sort-controls">
                              {/* Ascending Order (Older Posts First) */}
                              <div title="Sort Ascending (Older Posts First)">
                                <FontAwesomeIcon
                                  icon={faArrowUp}
                                  style={{
                                    color:
                                      sortOrders[platform] === "asc"
                                        ? "#63E6BE"
                                        : "#aaa",
                                    transition: "color 0.3s ease-in-out",
                                    fontSize: "16px",
                                  }}
                                  onClick={() => handleSortOrder(platform)}
                                />
                              </div>

                              {/* Descending Order (Latest Posts First) */}
                              <div title="Sort Descending (Latest Posts First)">
                                <FontAwesomeIcon
                                  icon={faArrowDown}
                                  style={{
                                    color:
                                      sortOrders[platform] === "desc"
                                        ? "#63E6BE"
                                        : "#aaa",
                                    transition: "color 0.3s ease-in-out",
                                    fontSize: "16px",
                                  }}
                                  onClick={() => handleSortOrder(platform)}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="card-line"></div>
                          <div className="OT-card-content">
                            {filteredPosts.map((post, index) => (
                              <div
                                className="OT-post-card"
                                key={`${searchTerm}-${platform}-${index}`}
                              >
                                {renderOriginatorText(searchTerm, platform, index)}
                                {renderOpenPostButton(post, platform)}

                                <div className="color-strip"></div>
                                <div className="header">
                                  <div className="username">
                                    {post.author_username || post.owner_username ? (
                                      <a
                                        href={`https://www.${platform}.com/${
                                          platform === "tiktok" ? "@" : ""
                                        }${
                                          post.author_username || post.owner_username
                                        }`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          textDecoration: "none",
                                          color: "#1DA1F2",
                                          fontWeight: "bold",
                                          display: "inline-block",
                                          wordWrap: "break-word",
                                          overflowWrap: "break-word",
                                          maxWidth: "100%",
                                        }}
                                      >
                                        @{post.author_username || post.owner_username}
                                      </a>
                                    ) : null}
                                  </div>

                                  <div className="name">
                                    {(post.author_full_name ||
                                      post.owner_full_name) && (
                                      <span>
                                        {post.author_full_name || post.owner_full_name}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                <div className="date-time">
                                  <span>{toPakistaniTime(post.timestamp)}</span>
                                </div>

                                <div className="OT-Card-content">
                                  {renderPostText(post.text, `${searchTerm}-${platform}-${index}`)}
                                  {renderPostImages(
                                    post["attached_media_display_url"] ||
                                      post["attached_medias_preview_url"] ||
                                      post["attached_medias_url"]
                                  )}
                                </div>

                                <div
                                  className={`OT-Card-stats platform-${post.platform}`}
                                >
                                  {renderPostStats(post)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    return null; // Do not render empty platform cards
                  })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OTCardContainer;